import { Router } from '@angular/router';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { TenantConfigurationService } from './tenant-configuration.service';
import { Organisation } from 'app/shared/models/organisation.model';
import { TenantConfiguration } from 'app/shared/models/tenant-configuration.model';
import { MessageService } from '../../services/messages.service';
import { ConstantsService } from 'app/services/constants-service';
import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';
import { Configuration } from 'app/shared/models/configuration.model';
import { SnackBarService } from 'app/services/snackbar.service';

@Component({
  selector: 'app-tenant',
  templateUrl: './tenant-configuration.component.html',
  styleUrls: ['./tenant-configuration.component.css']
})
export class TenantConfigurationComponent implements OnInit, OnDestroy {
  showHeadOfficeUpdateInputField: boolean = false;
  subOffices: Organisation[] = [];
  headOffice: Organisation;
  configs: TenantConfiguration[] = [];
  searchURI = ConstantsService.CLIENT_SEARCH;
  searchPlaceholder = MessageService.ORGANISATION_SEARCH_PLACEHOLDER;

  /** Mirrors Beamar adverse taxonomy category ids (display labels). */
  readonly adverseTaxonomyCategories = [
    'AML / CFT',
    'Corruption & bribery',
    'Fraud',
    'Sanctions evasion',
    'Organized crime',
    'Terrorism',
    'Human rights abuse',
    'Regulatory enforcement',
  ];

  isAdverseMediaGroup(group: TenantConfiguration): boolean {
    return !!group && String(group.contextName || '').trim().toLowerCase() === 'bad press';
  }

  tabLabelFor(group: TenantConfiguration): string {
    if (this.isAdverseMediaGroup(group)) {
      return 'Adverse media';
    }
    return group && group.contextName ? group.contextName : '';
  }

  displayConfigName(configuration: Configuration): string {
    if (!configuration) {
      return '';
    }
    if (String(configuration.name || '').toLowerCase() === 'bad press keyword') {
      return 'Optional extra search terms';
    }
    return configuration.name;
  }

  displayConfigPlaceholder(configuration: Configuration): string {
    if (configuration && String(configuration.name || '').toLowerCase() === 'bad press keyword') {
      return 'e.g. crypto mixer, trade-based laundering, shipping';
    }
    return configuration && configuration.description ? configuration.description : '';
  }

  displayConfigHelp(configuration: Configuration): string {
    if (configuration && String(configuration.name || '').toLowerCase() === 'bad press keyword') {
      return 'Comma-separated extras for your book of business. Terms already covered by the '
        + 'built-in taxonomy (e.g. fraud, bribery) are ignored automatically. Leave blank to rely on taxonomy only.';
    }
    return configuration && configuration.description ? configuration.description : '';
  }
  constructor(
    private tenantConfigurationService: TenantConfigurationService,
    private snackBarService: SnackBarService,
    private router: Router,
  ) { }

  ngOnInit() {
    this.getTenantOfficesList();
    this.getTenantConfigurations();
    // Check for ongoing migrations/verifications and resume tracking if needed
    this.checkAndResumeMigrationStatus();
    this.checkAndResumeVerificationStatus();
  }

  toggleShowHeadOfficeUpdateInputFieldFlag() {
    this.showHeadOfficeUpdateInputField = !this.showHeadOfficeUpdateInputField;
  }

  getTenantConfigurations() {
    this.tenantConfigurationService.getTenantConfigurations()
      .subscribe(response => {
        this.configs = response;
      });
  }

  updateHeadOffice(org: Organisation) {
    this.tenantConfigurationService.setHeadOffice(org.id)
      .subscribe(response => {
        if (response.flagResult) {
          this.headOffice = org;
          this.callSnackBarService(response, MessageService.UPDATE_TENANT_HEADOFFICE_SUCCESS, null);
        }
      }, err => {
        this.callSnackBarService(err, null, MessageService.UPDATE_TENANT_HEADOFFICE_FAILURE);
      });
  }

  addSubOffice(org: Organisation) {
    this.tenantConfigurationService.addSubOffice(org.id)
      .subscribe(response => {
        if (response.flagResult) {
          this.subOffices.push(org)
          this.callSnackBarService(response, MessageService.UPDATE_TENANT_SUBOFFICE_SUCCESS, null);
        }
      }, err => {
        this.callSnackBarService(err, null, MessageService.UPDATE_TENANT_SUBOFFICE_FAILURE);
      });
  }

  updateConfigurations() {
    let allConfigs: Configuration[] = [];
    this.configs.forEach(tenantConfiguration => {
      allConfigs = allConfigs.concat(tenantConfiguration.configurations);
    });
    this.tenantConfigurationService.updateConfigs(allConfigs).subscribe(response => {
      this.callSnackBarService(response, MessageService.UPDATE_TENANT_CONFIGURATION_SUCCESS, null);
    }, err => {
      this.callSnackBarService(err, null, MessageService.UPDATE_TENANT_CONFIGURATION_FAILURE);
    });
  }

  getTenantOfficesList() {
    this.tenantConfigurationService.getOffices().subscribe(response => {
      this.subOffices = response.subOffices;
      this.headOffice = response.headOffice;
    });
  }

  deleteSubOffice(office: Organisation) {
    this.tenantConfigurationService.deleteSubOffice(office.id).subscribe(response => {
      if (response.flagResult) {
        this.subOffices.splice(this.subOffices.indexOf(office), 1);
      }
      this.callSnackBarService(response, MessageService.DELETE_TENANT_SUBOFFICE_SUCCESS, null);
    }, err => {
      this.callSnackBarService(err, null, MessageService.DELETE_TENANT_SUBOFFICE_FAILURE);
    });
  }

  onClientSelectionEvent(searchDetails: GenericSearchDetails) {
    const headOffice = new Organisation();
    headOffice.id = searchDetails.id;
    headOffice.name = searchDetails.clientName;
    this.updateHeadOffice(headOffice);
  }

  checkIfSubOfficeExists(id: number) {
    let count = this.subOffices.filter(office => office.id === id).length;
    return count > 0;
  }

  onClientsubOfficeSelectionEvent(searchDetails: GenericSearchDetails) {
    const subOffice = new Organisation();
    subOffice.id = searchDetails.id;
    subOffice.name = searchDetails.clientName;
    if (this.checkIfSubOfficeExists(subOffice.id)) {
      this.callSnackBarService({}, MessageService.DELETE_TENANT_SUBOFFICE_EXISTS, MessageService.DELETE_TENANT_SUBOFFICE_EXISTS);
    }
    else {
      this.addSubOffice(subOffice);
    }
  }

  callSnackBarService(response: any, successMsg: string, failureMsg: string) {
    this.snackBarService.showStatus(response, successMsg, failureMsg);
  }

  downloadSubOfficeISIReport2017(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2017
        .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

  downloadSubOfficeISIReport2018(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2018
        .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

 downloadSubOfficeISIReport2019(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2019
        .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

  downloadSubOfficeISIReport2020(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2020
        .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

  downloadSubOfficeISIReport2021(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2021
      .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

  downloadSubOfficeISIReport2022(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2022
      .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

  downloadSubOfficeISIReport2023(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2023
      .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

  downloadSubOfficeISIReport2024(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2024
      .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

  downloadSubOfficeISIReport2025(org: Organisation) {
    const reportUrl = ConstantsService.PARAM_ISI_SUB_2025
      .replace(/ENTID/, org.id.toString()).replace(/REPORTNAME/, org.name.replace(/\s+/, '_'));
    window.open(ConstantsService.APP_BE_URL + ConstantsService.PRINT_REPORT_SERVLET + reportUrl);
  }

  routeToComponent(org: Organisation, year: number) {

    this.router.navigateByUrl(ConstantsService.MAIN_ISI_COMPONENT_ROUTE + '/' + org.id + '/' + year);
  }

  // S3 Migration properties
  migrationInProgress: boolean = false;
  verificationInProgress: boolean = false;
  migrationStats: any = null;
  verificationStats: any = null;
  migrationProgress: number = 0;
  verificationProgress: number = 0;
  private migrationPollInterval: any = null;
  private verificationPollInterval: any = null;
  
  // Test document properties
  testInProgress: boolean = false;
  testResult: any = null;

  screeningRegistrationInProgress = false;
  screeningRegistrationResult: any = null;

  bankStatementCleanupBeforeDate = '2026-01-11';
  bankStatementCleanupPreviewInProgress = false;
  bankStatementCleanupInProgress = false;
  bankStatementCleanupPreview: any = null;
  bankStatementCleanupResult: any = null;

  screeningWorkflowCleanupPreviewInProgress = false;
  screeningWorkflowCleanupInProgress = false;
  screeningWorkflowCleanupPreview: any = null;
  screeningWorkflowCleanupResult: any = null;

  /**
   * Batch-registers relation entities with the screening service (Beamar) for this tenant.
   */
  startScreeningRegistrationBatch() {
    this.screeningRegistrationInProgress = true;
    this.screeningRegistrationResult = null;
    this.tenantConfigurationService.registerAllEntitiesForScreening()
      .subscribe(
        (response: any) => {
          this.screeningRegistrationInProgress = false;
          const r = response && response.arrResult != null ? response.arrResult : response;
          this.screeningRegistrationResult = r || {};
          const msg =
            r && r.message ? r.message : 'Screening registration batch finished.';
          this.snackBarService.showSimpleMsj(msg);
        },
        (err: any) => {
          this.screeningRegistrationInProgress = false;
          this.callSnackBarService(err, null, 'Screening registration failed: ' + (err.message || 'Unknown error'));
        }
      );
  }

  /**
   * Starts the migration of existing documents from database to S3.
   */
  startMigration() {
    this.migrationInProgress = true;
    this.migrationStats = null;
    this.migrationProgress = 0;
    
    // Stop any existing polling
    if (this.migrationPollInterval) {
      clearInterval(this.migrationPollInterval);
    }
    
    this.tenantConfigurationService.migrateDocumentsToS3()
      .subscribe(
        response => {
          if (response && response.arrResult) {
            if (response.arrResult.status === 'started' || response.arrResult.status === 'already_running') {
              // Start polling for status
              this.pollMigrationStatus();
            } else {
              // Migration completed immediately (shouldn't happen for long-running tasks)
              this.migrationInProgress = false;
              this.migrationStats = response.arrResult;
            }
          }
        },
        err => {
          this.migrationInProgress = false;
          this.callSnackBarService(err, null, 'Failed to start migration: ' + (err.message || 'Unknown error'));
        }
      );
  }

  /**
   * Checks if there's an ongoing migration and resumes tracking if needed.
   * Called on component initialization and after connection errors.
   */
  private checkAndResumeMigrationStatus() {
    this.tenantConfigurationService.getMigrationStatus()
      .subscribe(
        response => {
          if (response && response.arrResult) {
            const status = response.arrResult;
            
            if (status.status === 'running') {
              // Migration is still running, resume tracking
              this.migrationInProgress = true;
              this.migrationStats = status;
              
              if (status.progressPercentage !== undefined) {
                this.migrationProgress = Math.round(status.progressPercentage);
              }
              
              // Start polling if not already polling
              if (!this.migrationPollInterval) {
                this.pollMigrationStatus();
              }
            } else if (status.status === 'completed' || status.status === 'failed') {
              // Migration finished, show final status
              this.migrationInProgress = false;
              this.migrationStats = status;
              
              if (status.progressPercentage !== undefined) {
                this.migrationProgress = Math.round(status.progressPercentage);
              }
            }
            // If status is 'not_started', do nothing
          }
        },
        err => {
          // Silently fail on initial check - migration might not have started yet
          // Don't show error message on initial load
        }
      );
  }

  /**
   * Polls for migration status until completion.
   * Handles connection errors gracefully by retrying instead of stopping.
   */
  private pollMigrationStatus() {
    // Clear any existing interval first
    if (this.migrationPollInterval) {
      clearInterval(this.migrationPollInterval);
    }
    
    this.migrationPollInterval = setInterval(() => {
      this.tenantConfigurationService.getMigrationStatus()
        .subscribe(
          response => {
            if (response && response.arrResult) {
              const status = response.arrResult;
              this.migrationStats = status;
              
              if (status.progressPercentage !== undefined) {
                this.migrationProgress = Math.round(status.progressPercentage);
              }
              
              if (status.status === 'completed' || status.status === 'failed') {
                // Stop polling
                clearInterval(this.migrationPollInterval);
                this.migrationPollInterval = null;
                this.migrationInProgress = false;
                
                if (status.status === 'completed') {
                  this.callSnackBarService(response, status.message || 'Migration completed successfully', null);
                } else {
                  this.callSnackBarService(response, null, status.message || status.error || 'Migration failed');
                }
              } else if (status.status === 'running') {
                // Ensure we're still tracking
                this.migrationInProgress = true;
              }
            }
          },
          err => {
            // On connection error, don't stop polling - just log and continue
            // The next poll will retry automatically
            console.warn('Error checking migration status (will retry):', err);
            // Keep polling - don't clear the interval
            // This allows automatic reconnection when the connection is restored
          }
        );
    }, 2000); // Poll every 2 seconds
  }

  /**
   * Verifies that migrated documents exist in S3.
   */
  verifyMigration() {
    this.verificationInProgress = true;
    this.verificationStats = null;
    this.verificationProgress = 0;
    
    // Stop any existing polling
    if (this.verificationPollInterval) {
      clearInterval(this.verificationPollInterval);
    }
    
    this.tenantConfigurationService.verifyDocumentMigration()
      .subscribe(
        response => {
          if (response && response.arrResult) {
            if (response.arrResult.status === 'started' || response.arrResult.status === 'already_running') {
              // Start polling for status
              this.pollVerificationStatus();
            } else {
              // Verification completed immediately
              this.verificationInProgress = false;
              this.verificationStats = response.arrResult;
            }
          }
        },
        err => {
          this.verificationInProgress = false;
          this.callSnackBarService(err, null, 'Failed to start verification: ' + (err.message || 'Unknown error'));
        }
      );
  }

  /**
   * Checks if there's an ongoing verification and resumes tracking if needed.
   * Called on component initialization and after connection errors.
   */
  private checkAndResumeVerificationStatus() {
    this.tenantConfigurationService.getVerificationStatus()
      .subscribe(
        response => {
          if (response && response.arrResult) {
            const status = response.arrResult;
            
            if (status.status === 'running') {
              // Verification is still running, resume tracking
              this.verificationInProgress = true;
              this.verificationStats = status;
              
              if (status.progressPercentage !== undefined) {
                this.verificationProgress = Math.round(status.progressPercentage);
              }
              
              // Start polling if not already polling
              if (!this.verificationPollInterval) {
                this.pollVerificationStatus();
              }
            } else if (status.status === 'completed' || status.status === 'failed') {
              // Verification finished, show final status
              this.verificationInProgress = false;
              this.verificationStats = status;
              
              if (status.progressPercentage !== undefined) {
                this.verificationProgress = Math.round(status.progressPercentage);
              }
            }
            // If status is 'not_started', do nothing
          }
        },
        err => {
          // Silently fail on initial check - verification might not have started yet
          // Don't show error message on initial load
        }
      );
  }

  /**
   * Polls for verification status until completion.
   * Handles connection errors gracefully by retrying instead of stopping.
   */
  private pollVerificationStatus() {
    // Clear any existing interval first
    if (this.verificationPollInterval) {
      clearInterval(this.verificationPollInterval);
    }
    
    this.verificationPollInterval = setInterval(() => {
      this.tenantConfigurationService.getVerificationStatus()
        .subscribe(
          response => {
            if (response && response.arrResult) {
              const status = response.arrResult;
              this.verificationStats = status;
              
              if (status.progressPercentage !== undefined) {
                this.verificationProgress = Math.round(status.progressPercentage);
              }
              
              if (status.status === 'completed' || status.status === 'failed') {
                // Stop polling
                clearInterval(this.verificationPollInterval);
                this.verificationPollInterval = null;
                this.verificationInProgress = false;
                
                if (status.status === 'completed') {
                  this.callSnackBarService(response, status.message || 'Verification completed successfully', null);
                } else {
                  this.callSnackBarService(response, null, status.message || status.error || 'Verification failed');
                }
              } else if (status.status === 'running') {
                // Ensure we're still tracking
                this.verificationInProgress = true;
              }
            }
          },
          err => {
            // On connection error, don't stop polling - just log and continue
            // The next poll will retry automatically
            console.warn('Error checking verification status (will retry):', err);
            // Keep polling - don't clear the interval
            // This allows automatic reconnection when the connection is restored
          }
        );
    }, 2000); // Poll every 2 seconds
  }

  /**
   * Tests uploading a single document to S3 without updating the database record.
   * Automatically selects the first unmigrated document.
   */
  testDocumentUpload() {
    this.testInProgress = true;
    this.testResult = null;
    
    this.tenantConfigurationService.testDocumentUpload()
      .subscribe(
        response => {
          this.testInProgress = false;
          this.testResult = response;
          if (response.success) {
            this.callSnackBarService(response, 'Document test upload successful', null);
          } else {
            this.callSnackBarService(response, null, response.error || 'Document test upload failed');
          }
        },
        err => {
          this.testInProgress = false;
          this.testResult = err;
          this.callSnackBarService(err, null, 'Error testing document upload: ' + (err.message || err.error || 'Unknown error'));
        }
      );
  }

  previewBankStatementCleanup() {
    if (!this.bankStatementCleanupBeforeDate) {
      this.snackBarService.showSimpleMsj('Please select a removal date.');
      return;
    }

    this.bankStatementCleanupPreviewInProgress = true;
    this.bankStatementCleanupPreview = null;
    this.bankStatementCleanupResult = null;

    this.tenantConfigurationService.previewBankStatementCleanup(this.bankStatementCleanupBeforeDate)
      .subscribe(
        (response: any) => {
          this.bankStatementCleanupPreviewInProgress = false;
          this.bankStatementCleanupPreview = response && response.arrResult != null ? response.arrResult : response;
          const preview = this.bankStatementCleanupPreview || {};
          const msg = preview.message
            || ('Preview: ' + (preview.statementsToDelete || 0) + ' statement(s), '
              + (preview.transactionsToDelete || 0) + ' transaction(s).');
          this.snackBarService.showSimpleMsj(msg);
        },
        (err: any) => {
          this.bankStatementCleanupPreviewInProgress = false;
          this.callSnackBarService(err, null, 'Bank statement cleanup preview failed: ' + (err.message || 'Unknown error'));
        }
      );
  }

  startBankStatementCleanup() {
    if (!this.bankStatementCleanupBeforeDate) {
      this.snackBarService.showSimpleMsj('Please select a removal date.');
      return;
    }

    const preview = this.bankStatementCleanupPreview;
    const statementsToDelete = preview ? preview.statementsToDelete : null;
    const transactionsToDelete = preview ? preview.transactionsToDelete : null;
    const confirmMessage = statementsToDelete != null
      ? 'Delete ' + statementsToDelete + ' bank statement(s) and ' + transactionsToDelete
        + ' linked transaction(s) ending on or before ' + this.bankStatementCleanupBeforeDate
        + ', including PDF files? This cannot be undone.'
      : 'Delete all bank statements ending on or before ' + this.bankStatementCleanupBeforeDate
        + ', including linked transactions and PDF files? This cannot be undone.';

    if (!confirm(confirmMessage)) {
      return;
    }

    this.bankStatementCleanupInProgress = true;
    this.bankStatementCleanupResult = null;

    this.tenantConfigurationService.cleanupBankStatements(this.bankStatementCleanupBeforeDate)
      .subscribe(
        (response: any) => {
          this.bankStatementCleanupInProgress = false;
          this.bankStatementCleanupResult = response && response.arrResult != null ? response.arrResult : response;
          this.bankStatementCleanupPreview = null;
          const result = this.bankStatementCleanupResult || {};
          const msg = result.message
            || ('Deleted ' + (result.statementsDeleted || 0) + ' statement(s) and '
              + (result.transactionsDeleted || 0) + ' transaction(s).');
          this.snackBarService.showSimpleMsj(msg);
        },
        (err: any) => {
          this.bankStatementCleanupInProgress = false;
          this.callSnackBarService(err, null, 'Bank statement cleanup failed: ' + (err.message || 'Unknown error'));
        }
      );
  }

  previewScreeningWorkflowCleanup() {
    this.screeningWorkflowCleanupPreviewInProgress = true;
    this.screeningWorkflowCleanupPreview = null;
    this.screeningWorkflowCleanupResult = null;

    this.tenantConfigurationService.previewScreeningWorkflowCleanup()
      .subscribe(
        (response: any) => {
          this.screeningWorkflowCleanupPreviewInProgress = false;
          this.screeningWorkflowCleanupPreview =
            response && response.arrResult != null ? response.arrResult : response;
          const preview = this.screeningWorkflowCleanupPreview || {};
          const msg = preview.message || 'Screening workflow cleanup preview ready.';
          this.snackBarService.showSimpleMsj(msg);
        },
        (err: any) => {
          this.screeningWorkflowCleanupPreviewInProgress = false;
          this.callSnackBarService(
            err,
            null,
            'Screening workflow cleanup preview failed: ' + (err.message || 'Unknown error'));
        }
      );
  }

  startScreeningWorkflowCleanup() {
    const preview = this.screeningWorkflowCleanupPreview;
    const openBpmn = preview ? preview.openBpmnWorkflows : null;
    const confirmMessage = preview
      ? 'Close ' + (openBpmn || 0) + ' legacy BPMN workflow(s) and remove '
        + (preview.openBpmnTasks || 0) + ' Activiti task(s)? Match lists will not be changed. '
        + 'This cannot be undone.'
      : 'Close legacy sanction, PEP, and bad-press BPMN workflows and remove their Activiti '
        + 'tasks for this tenant? Match lists will not be changed. This cannot be undone.';

    if (!confirm(confirmMessage)) {
      return;
    }

    this.screeningWorkflowCleanupInProgress = true;
    this.screeningWorkflowCleanupResult = null;

    this.tenantConfigurationService.cleanupOpenScreeningWorkflows()
      .subscribe(
        (response: any) => {
          this.screeningWorkflowCleanupInProgress = false;
          this.screeningWorkflowCleanupResult =
            response && response.arrResult != null ? response.arrResult : response;
          this.screeningWorkflowCleanupPreview = null;
          const result = this.screeningWorkflowCleanupResult || {};
          const msg = result.message || 'Screening workflow cleanup finished.';
          this.snackBarService.showSimpleMsj(msg);
        },
        (err: any) => {
          this.screeningWorkflowCleanupInProgress = false;
          this.callSnackBarService(
            err,
            null,
            'Screening workflow cleanup failed: ' + (err.message || 'Unknown error'));
        }
      );
  }

  /**
   * Cleanup polling intervals on component destroy.
   */
  ngOnDestroy() {
    if (this.migrationPollInterval) {
      clearInterval(this.migrationPollInterval);
    }
    if (this.verificationPollInterval) {
      clearInterval(this.verificationPollInterval);
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/admin/tenantconfiguration/tenant-configuration.component.ts